import {
  BASIC_TYPE,
  InitDenoiserOptions
} from 'trtc-js-sdk-core/src/enums';
import { IPlugin, Core } from 'trtc-js-sdk-core/src/plugin';
import { ValidateRule } from 'trtc-js-sdk-core/src/utils/decorators/validate';

import RtcError from '../../common/rtc-error.js';
import { ErrorCode, ErrorCodeDictionary } from '../../common/error-code.js';
import { getAudioContext } from 'trtc-js-sdk-core/src/utils/audio-context.js';
import loadWorklet from 'trtc-js-sdk-core/src/common/volume-meter/worklet-loader.js';
import { DenoiserMode, NAME, UpdateDenoiserOptions, formatDate } from 'trtc-js-sdk-core';
import { isOverseaSdkAppId, isUndefined } from 'trtc-js-sdk-core/src/utils';
import { isSIMDSupported } from 'trtc-js-sdk-core/src/common/rtc-detection.js';
let adSeq = 0;
let preloadPromise: Promise<void>;

interface AIDenoiserConfig {
  enableFarFieldReduce: boolean;
  farFieldReduceThreshold: number;
}

export class AIDenoiser implements IPlugin {
  log: Core['log'];
  audioContext: AudioContext = getAudioContext('denoiser');
  workletNode?: AudioWorkletNode;
  config: AIDenoiserConfig = { enableFarFieldReduce: false, farFieldReduceThreshold: 0.5 };
  static startValidateRule(core: Core): ValidateRule {
    return {
      name: 'options',
      required: true,
      type: BASIC_TYPE.Object,
      properties: {
        sdkAppId: { type: BASIC_TYPE.Number, required: true },
        userId: { type: BASIC_TYPE.String, required: true },
        userSig: { type: BASIC_TYPE.String, required: true },
        mode: { type: BASIC_TYPE.Number, required: false, values: [DenoiserMode.NORMAL, DenoiserMode.FAR_FIELD_REDUCTION] },
        farFieldReduceThreshold: { type: BASIC_TYPE.Number, required: false, min: 0, max: 1 }
      },
      validate(params: any, key: string, fnName: string, className: string) {
        if (!core.room.audioManager.hasAudioTrack) {
          throw new RtcError({
            code: ErrorCode.INVALID_OPERATION,
            extraCode: ErrorCodeDictionary.INVALID_OPERATION_NEED_AUDIO,
            fnName
          });
        }
      }
    };
  }
  static updateValidateRule: ValidateRule = {
    type: BASIC_TYPE.Object,
    properties: {
      mode: { type: BASIC_TYPE.Number, required: false, values: [DenoiserMode.NORMAL, DenoiserMode.FAR_FIELD_REDUCTION] },
      farFieldReduceThreshold: { type: BASIC_TYPE.Number, required: false, min: 0, max: 1 }
    }
  };
  static stopValidateRule: ValidateRule = {
    type: BASIC_TYPE.Object
  };

  constructor(public core: Core) {
    adSeq = adSeq + 1;
    this.log = core.log.createChild({ id: `${this.getAlias()}${adSeq}` });
    this.log.info(`created id=${this.getAlias()}${adSeq}`);
  }

  private preload(path: string) {
    if (!preloadPromise) {
      preloadPromise = this.doPreload(path);
    }
    return preloadPromise;
  }

  private async doPreload(path: string) {
    const blob = await this.core.fileDownloader.download(path, { type: 'blob' }) as Blob;
    const moduleUrl = URL.createObjectURL(blob);
    try {
      await loadWorklet(this.audioContext, moduleUrl);
    } catch (error) {
      this.log.error('load worklet failed', error);
      throw error;
    } finally {
      URL.revokeObjectURL(moduleUrl);
    }
  }

  static Name = 'AIDenoiser';
  getName() {
    return AIDenoiser.Name;
  }

  getAlias(): string {
    return 'ad';
  }

  // 本地只允许一次
  getGroup(): string {
    return 'AIDenoiser';
  }

  getValidateRule(method: 'start' | 'update' | 'stop'): ValidateRule {
    switch (method) {
      case 'start':
        return AIDenoiser.startValidateRule(this.core);
      case 'update':
        return AIDenoiser.updateValidateRule;
      case 'stop':
        return AIDenoiser.stopValidateRule;
    }
  }

  async start(options: InitDenoiserOptions) {
    const { room, schedule } = this.core;
    const { assetsPath = this.core.assetsPath } = options;
    if (!assetsPath) throw new RtcError({
      code: ErrorCode.INVALID_PARAMETER,
      message: 'you need to deploy the assets of the npm package and set assetsPath param in TRTC.create()'
    });
    this.core.validateSourceNode();
    await this.preload(`${assetsPath}/denoiser-wasm${isSIMDSupported() ? '' : '-nosimd'}.js`);
    if (!this.workletNode) {
      const timestamp = String(Date.now()).slice(0, -3);
      const { auth, sign, status, message } = await httpAuth(schedule, { ...options, timestamp });
      // 1. 校验请求
      if (!auth) {
        this.log.info(`RTCAIDenoiser: ${options.userId} auth result: ${auth}. Message: ${message}`);
        throw new RtcError({
          code: ErrorCode.INVALID_PARAMETER,
          message
        });
      }
      this.workletNode = new AudioWorkletNode(this.audioContext, 'trtc-denoiser-processor', {
        numberOfInputs: 1,
        numberOfOutputs: 1
      });
      this.workletNode.port.postMessage({
        type: 'init',
        data: {
          sdkAppId: String(options.sdkAppId),
          userId: options.userId,
          timestamp,
          sign,
          status
        }
      });
      this.workletNode.port.onmessage = (e: MessageEvent) => {
        const { data } = e;
        if (data.type === 'cost') {
          // 打印每 30s 统计的处理耗时数据. data.hist = [a, b, c, d] 表示 30s 內处理耗时落在 0-5ms, 5-10ms, 10-20ms, 超过20ms的各区间百分比
          const logLevel = data?.max > 20 ? 'warn' : (data?.max > 10 ? 'info' : 'debug');
          this.log[logLevel](`avg cost: ${data.value} max: ${data?.max}(${formatDate(new Date(data?.maxCostTimestamp))}) hist: ${data?.hist?.join(' ')}`);
        } else if (data.type === 'log') {
          this.log[data.logLevel as 'debug' | 'info' | 'warn' | 'error'](`${data.value}`);
        }
      };
    }
    this.updateConfig(options);
    this.workletNode.port.postMessage({ type: 'enable' });
    room.audioManager.addDenoiser(this.workletNode);
    room.sendAbilityStatus({ ai_denoise: 1 });
  }

  async update(options: UpdateDenoiserOptions) {
    this.updateConfig(options);
  }


  async stop() {
    if (!this.workletNode) return;
    const { room } = this.core;
    this.workletNode.port.postMessage({ type: 'disable' });
    await room.audioManager.removeDenoiser(this.workletNode);
  }

  updateConfig(options: InitDenoiserOptions | UpdateDenoiserOptions) {
    if (!this.workletNode) return;
    let needToUpdateConfig = false;
    if (!isUndefined(options.mode)) {
      if (options.mode === DenoiserMode.NORMAL) this.config.enableFarFieldReduce = false;
      else if (options.mode === DenoiserMode.FAR_FIELD_REDUCTION) this.config.enableFarFieldReduce = true;
      needToUpdateConfig = true;
    }
    // @ts-ignore
    if (!isUndefined(options.farFieldReduceThreshold)) {
      // @ts-ignore
      this.config.farFieldReduceThreshold = options.farFieldReduceThreshold;
      needToUpdateConfig = true;
    }
    if (needToUpdateConfig) this.workletNode.port.postMessage({ type: 'setConfig', data: this.config });
  }

  destroy(): void {
    if (!this.workletNode) return;
    this.workletNode.port.onmessage = null;
  }
}

async function httpAuth(schedule: Core['schedule'], {
  sdkAppId,
  userId,
  userSig,
  timestamp
}: {
  sdkAppId: number,
  userId: string,
  userSig: string,
  timestamp: string;
}) {
  try {
    // 任一请求成功即可
    const { data: { errCode, errMsg, sign, status } } = await schedule.getAbilityConfig(sdkAppId, schedule.ScheduleRequestType.AUDIO_AI_AUTH, {
      sdkAppId,
      userId,
      userSig,
      timestamp
    });
    // code: 0, data: { errMsg, sign, status: '1'}, msg: 'Ok'
    if (status === '1') {
      return {
        auth: true,
        sign,
        status,
        message: errMsg
      };
    }
    const docPath = isOverseaSdkAppId(sdkAppId) ? 'https://trtc.io/document/42734?platform=web&product=rtcengine&menulabel=coresdk' : 'https://cloud.tencent.com/document/product/647/44247';
    const failed = 'Init RTCAIDenoiser failed.';
    let reason = '';
    switch (errCode) {
      case 1:
        reason = 'Please check your params.';
        break;
      case 2:
        reason = `You need to buy packages. Refer to: ${docPath}`;
        break;
      case 3:
        reason = 'Server is invalid. Please contact our engineer. ';
        break;
      case 4:
        reason = `Your packages is not active. Refer to: ${docPath}`;
        break;
      case 5:
        reason = `Your packages is expired. Refer to: ${docPath}`;
        break;
      case 6:
        reason = 'Your version is not supported.';
        break;
    }
    return {
      auth: false,
      status,
      message: errMsg ? `${failed} Reason: ${errMsg}. ${reason}` : `${failed}, ${reason}`
    };
  } catch (error) {
    // 所有请求都失败的情况
    return {
      auth: false,
      status: '0',
      message: `Init RTCAIDenoiser failed. All requests failed. ${error}`
    };
  }
}
